import React, { useMemo, useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  useTheme,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import {
  BarChart,
  ComposedChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  TrendingUp,
  CalendarToday,
  Star,
  CloudDone,
  Assignment,
  LocalOffer,
  AccessTime,
} from '@mui/icons-material'
import { useList } from '@refinedev/core'
import { useNavigate, useLocation } from 'react-router-dom'

import {
  DashboardData,
  MonthlyRevenueData,
  MetricCardProps,
  RevenueBreakdownData,
  RevenueBreakdownResponse,
  SubscriptionBreakdownData,
  SubscriptionBreakdownResponse,
} from '../interfaces/accountsInterfaces'
import XeroConnectionStatus from './XeroConnectionStatus'
import { ReconciliationDashboard } from './ReconciliationDashboard'
import RevenueBreakdownModal from './RevenueBreakdownModal'
import SubscriptionBreakdownModal from './SubscriptionBreakdownModal'
import DiscountCodesManagement from './DiscountCodesManagement'
import ExpiredDiscountsView from './ExpiredDiscountsView'
import ActiveDiscountsView from './ActiveDiscountsView'
import OnboardingStatsDashboard from './OnboardingStatsDashboard'
import {
  getRevenueBreakdown,
  getSubscriptionBreakdown,
} from '../utils/forecastApi'
import ActivityFeedAll from './ActivityFeedAll'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`accounts-tabpanel-${index}`}
      aria-labelledby={`accounts-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

interface AccountsDashboardProps {
  initialTab?: string
}

const AccountsDashboard: React.FC<AccountsDashboardProps> = ({
  initialTab,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  // Map tab names to indices
  const tabMap = {
    financial: 0,
    xero: 1,
    reconciliation: 2,
    discounts: 3,
    onboarding: 4,
    activity: 5,
  }

  const tabPaths = {
    0: '/accounts/financial',
    1: '/accounts/xero',
    2: '/accounts/reconciliation',
    3: '/accounts/discounts',
    4: '/accounts/onboarding',
    5: '/accounts/activity',
  }

  // Determine initial tab from URL or prop
  const getInitialTab = () => {
    if (initialTab && tabMap[initialTab as keyof typeof tabMap] !== undefined) {
      return tabMap[initialTab as keyof typeof tabMap]
    }

    // Check URL path
    const path = location.pathname
    if (path.includes('/accounts/financial')) return 0
    if (path.includes('/accounts/xero')) return 1
    if (path.includes('/accounts/reconciliation')) return 2
    if (path.includes('/accounts/discounts')) return 3
    if (path.includes('/accounts/onboarding')) return 4
    if (path.includes('/accounts/activity')) return 5
    // Default to financial overview
    return 0
  }

  const [currentTab, setCurrentTab] = useState(getInitialTab)
  const [headlineChartTab, setHeadlineChartTab] = useState(0)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Update tab when URL changes
  useEffect(() => {
    const newTab = getInitialTab()
    if (newTab !== currentTab) {
      setCurrentTab(newTab)
    }
  }, [location.pathname])

  // Modal state for revenue breakdown
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState<string>('')
  const [revenueBreakdownData, setRevenueBreakdownData] =
    useState<RevenueBreakdownData | null>(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalError, setModalError] = useState<string>('')

  // Modal state for subscription breakdown
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false)
  const [subscriptionBreakdownData, setSubscriptionBreakdownData] =
    useState<SubscriptionBreakdownData | null>(null)
  const [subscriptionModalLoading, setSubscriptionModalLoading] =
    useState(false)
  const [subscriptionModalError, setSubscriptionModalError] =
    useState<string>('')

  const {
    data: dashboardResponse,
    isLoading,
    isError,
    refetch,
  } = useList<DashboardData>({
    resource: 'accounts',
  })

  const dashboardData = dashboardResponse?.data?.[0] || null

  // Calculate monthly revenue data with recurring invoice forecasting
  const { revenueData } = useMemo(() => {
    // Create a map for forecast data
    const forecastMap = new Map<string, number>()
    if (dashboardData?.revenue_forecast) {
      dashboardData.revenue_forecast.forEach((forecast) => {
        forecastMap.set(forecast.month, forecast.forecast_revenue)
      })
    }

    const processedData: MonthlyRevenueData[] = []

    // Process historical monthly revenue data - now with account code separation
    if (
      dashboardData?.monthly_revenue_by_code &&
      dashboardData.monthly_revenue_by_code.length > 0
    ) {
      dashboardData.monthly_revenue_by_code.forEach((monthData) => {
        const monthKey = monthData.month
        const revenue200 = parseFloat(String(monthData.revenue_200 || 0))
        const revenueData = parseFloat(String(monthData.revenue_data || 0))
        const revenueOther = parseFloat(String(monthData.revenue_other || 0))
        const forecastRevenue = forecastMap.get(monthKey) || 0

        processedData.push({
          month: monthKey,
          revenue: revenue200, // Code 200 revenue (will be green)
          revenueData: revenueData, // Code 202 revenue (will be purple)
          revenueOther: revenueOther, // Other non-200/non-202 revenue (will be blue)
          forecast: forecastRevenue,
          total: revenue200 + revenueData + revenueOther + forecastRevenue,
          displayMonth: (() => {
            const [year, month] = monthKey.split('-')
            const date = new Date(parseInt(year), parseInt(month) - 1)
            return date.toLocaleDateString('en-US', {
              month: 'short',
              year: '2-digit',
            })
          })(),
        })
      })
    } else if (
      dashboardData?.monthly_revenue &&
      dashboardData.monthly_revenue.length > 0
    ) {
      // Fallback to old data structure if new one isn't available
      dashboardData.monthly_revenue.forEach((monthData) => {
        const monthKey = monthData.month
        const revenue = parseFloat(String(monthData.recognised_revenue || 0))
        const forecastRevenue = forecastMap.get(monthKey) || 0

        processedData.push({
          month: monthKey,
          revenue: revenue,
          revenueData: 0, // No data revenue in old structure
          revenueOther: 0, // No other revenue in old structure
          forecast: forecastRevenue,
          total: revenue + forecastRevenue,
          displayMonth: (() => {
            const [year, month] = monthKey.split('-')
            const date = new Date(parseInt(year), parseInt(month) - 1)
            return date.toLocaleDateString('en-US', {
              month: 'short',
              year: '2-digit',
            })
          })(),
        })
      })
    }

    // Add future months with only forecast revenue (next 12 months)
    const currentMonth = new Date()
    for (let i = 1; i <= 12; i++) {
      const futureDate = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + i,
        1
      )
      // Create monthKey consistently to avoid timezone issues
      const year = futureDate.getFullYear()
      const month = String(futureDate.getMonth() + 1).padStart(2, '0')
      const futureMonthKey = `${year}-${month}`

      const forecastRevenue = forecastMap.get(futureMonthKey) || 0

      // Only add future months that have forecast revenue > 0
      if (forecastRevenue > 0) {
        processedData.push({
          month: futureMonthKey,
          revenue: 0, // No historical revenue for future months
          revenueData: 0, // No data revenue for future months
          revenueOther: 0, // No other revenue for future months
          forecast: forecastRevenue,
          total: forecastRevenue,
          displayMonth: futureDate.toLocaleDateString('en-US', {
            month: 'short',
            year: '2-digit',
          }),
        })
      }
    }

    // Sort by month and limit to reasonable timeframe (last 6 months + next 12 months)
    const sorted = processedData.sort((a, b) => a.month.localeCompare(b.month))

    const sixMonthsAgoDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 6,
      1
    )
    const sixMonthsAgoYear = sixMonthsAgoDate.getFullYear()
    const sixMonthsAgoMonth = sixMonthsAgoDate.getMonth() + 1
    const sixMonthsAgo = `${sixMonthsAgoYear}-${String(
      sixMonthsAgoMonth
    ).padStart(2, '0')}`

    const filteredData = sorted.filter((data) => data.month >= sixMonthsAgo)

    // Calculate max revenue for the invisible click area
    const maxTotal = Math.max(...filteredData.map((data) => data.total), 0)
    const maxRevenueValue = maxTotal > 0 ? maxTotal * 1.1 : 100 // Add 10% padding for better click area

    // Add clickable area data to each month
    const dataWithClickArea = filteredData.map((data) => ({
      ...data,
      clickableArea: Math.max(0, maxRevenueValue - data.total), // Fill space above the bars, ensure non-negative
    }))

    return { revenueData: dataWithClickArea, maxRevenue: maxRevenueValue }
  }, [
    dashboardData?.monthly_revenue,
    dashboardData?.monthly_revenue_by_code,
    dashboardData?.revenue_forecast,
  ])

  const arrMovementData = useMemo(
    () =>
      (dashboardData?.arr_movement_history || []).map((row) => ({
        ...row,
        lost_arr_negative: -Math.abs(row.lost_arr || 0),
      })),
    [dashboardData?.arr_movement_history]
  )

  // Removed prepaid revenue - all revenue is now recognized immediately

  // Process subscription data into a table format
  const subscriptionTableData = useMemo(() => {
    if (!dashboardData?.subscription_breakdown)
      return {
        tiers: [],
        totals: { monthly: 0, quarterly: 0, yearly: 0, total_arr: 0 },
      }

    const tiers = new Map()
    const totals = { monthly: 0, quarterly: 0, yearly: 0, total_arr: 0 }

    // Initialize all standard tiers so they always appear in the table
    const standardTiers = [
      'Tier 1',
      'Tier 2',
      'Tier 3',
      'Tier 4',
      'Tier Micro',
      'Custom',
    ]
    standardTiers.forEach((tierName) => {
      tiers.set(tierName, {
        monthly: 0,
        quarterly: 0,
        yearly: 0,
        total_arr: 0,
      })
    })

    dashboardData.subscription_breakdown.forEach((plan) => {
      // Extract tier info from plan name
      let tierName = 'Custom'
      let frequency = 'Monthly'

      if (plan.plan_name.startsWith('Custom')) {
        tierName = 'Custom'
      } else if (plan.plan_name.includes('Tier Micro')) {
        tierName = 'Tier Micro'
      } else if (plan.plan_name.includes('Tier')) {
        const tierMatch = plan.plan_name.match(/Tier (\d+)/)
        tierName = tierMatch ? `Tier ${tierMatch[1]}` : 'Unknown'
      }

      // Extract frequency
      if (plan.plan_name.includes('Monthly')) {
        frequency = 'Monthly'
      } else if (plan.plan_name.includes('Quarterly')) {
        frequency = 'Quarterly'
      } else if (
        plan.plan_name.includes('Annual') ||
        plan.plan_name.includes('Yearly')
      ) {
        frequency = 'Yearly'
      }

      if (!tiers.has(tierName)) {
        tiers.set(tierName, {
          monthly: 0,
          quarterly: 0,
          yearly: 0,
          total_arr: 0,
        })
      }

      const tierData = tiers.get(tierName)
      const freqKey = frequency.toLowerCase()

      if (
        freqKey === 'monthly' ||
        freqKey === 'quarterly' ||
        freqKey === 'yearly'
      ) {
        tierData[freqKey] += plan.customer_count
        tierData.total_arr += plan.arr_per_plan

        totals[freqKey] += plan.customer_count
        totals.total_arr += plan.arr_per_plan
      }
    })

    return {
      tiers: Array.from(tiers.entries()).map(([name, data]) => ({
        name,
        ...data,
      })),
      totals,
    }
  }, [dashboardData?.subscription_breakdown])

  const MetricCard: React.FC<MetricCardProps> = ({
    title,
    value,
    icon: Icon,
    color = 'primary',
    subtitle,
  }) => (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {typeof value === 'number' ? `$${value.toLocaleString()}` : value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Icon
            sx={{
              fontSize: 40,
              color: theme.palette[color].main,
              opacity: 0.7,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  )

  const formatCurrency = (value: number): string => `$${value.toLocaleString()}`

  const formatArrMovementTooltip = (
    value: number,
    name: string
  ): [string, string] => {
    if (name.toLowerCase().includes('org count')) {
      return [Math.round(value).toLocaleString(), name]
    }
    const displayValue =
      name === 'Lost ARR' ? Math.abs(value) : value
    return [formatCurrency(Math.round(displayValue)), name]
  }

  const formatTooltip = (
    value: number,
    name: string
  ): [string, string] | [null, null] => {
    if (name === 'Forecast Revenue' && value === 0) {
      return [null, null] // Don't show forecast line when it's $0
    }
    return [formatCurrency(value), name]
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error loading dashboard data. Please try again.
        </Typography>
      </Box>
    )
  }

  const loadRevenueBreakdown = async (month: string) => {
    setSelectedMonth(month)
    setModalOpen(true)
    setModalLoading(true)
    setModalError('')
    setRevenueBreakdownData(null)

    try {
      const response: RevenueBreakdownResponse = await getRevenueBreakdown(
        month
      )
      setRevenueBreakdownData(response.data)
    } catch (error) {
      console.error('Error fetching revenue breakdown:', error)
      setModalError('Failed to load revenue details. Please try again.')
    } finally {
      setModalLoading(false)
    }
  }

  // Handle revenue bar clicks (works for both recognized and forecast)
  const handleRevenueBarClick = async (data: any) => {
    if (!data?.payload?.month) return
    await loadRevenueBreakdown(data.payload.month)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedMonth('')
    setRevenueBreakdownData(null)
    setModalError('')
  }

  const handleOpenSubscriptionModal = async () => {
    setSubscriptionModalOpen(true)
    setSubscriptionModalLoading(true)
    setSubscriptionModalError('')
    setSubscriptionBreakdownData(null)

    try {
      const response: SubscriptionBreakdownResponse =
        await getSubscriptionBreakdown()
      setSubscriptionBreakdownData(response.data)
    } catch (error) {
      console.error('Error fetching subscription breakdown:', error)
      setSubscriptionModalError(
        'Failed to load subscription details. Please try again.'
      )
    } finally {
      setSubscriptionModalLoading(false)
    }
  }

  const handleCloseSubscriptionModal = () => {
    setSubscriptionModalOpen(false)
    setSubscriptionBreakdownData(null)
    setSubscriptionModalError('')
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)

    // Navigate to the corresponding URL
    const newPath = tabPaths[newValue as keyof typeof tabPaths]
    if (newPath && location.pathname !== newPath) {
      navigate(newPath)
    }

    // Refetch data when switching to Financial Overview tab
    if (newValue === 0 && refetch) {
      refetch()
    }
  }

  const handleDataChange = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Accounts Dashboard
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="accounts dashboard tabs"
        >
          <Tab
            icon={<TrendingUp />}
            iconPosition="start"
            label="Financial Overview"
            id="accounts-tab-0"
            aria-controls="accounts-tabpanel-0"
          />
          <Tab
            icon={<CloudDone />}
            iconPosition="start"
            label="Xero Integration"
            id="accounts-tab-1"
            aria-controls="accounts-tabpanel-1"
          />
          <Tab
            icon={<Assignment />}
            iconPosition="start"
            label="Reconciliation"
            id="accounts-tab-2"
            aria-controls="accounts-tabpanel-2"
          />
          <Tab
            icon={<LocalOffer />}
            iconPosition="start"
            label="Discount Codes"
            id="accounts-tab-3"
            aria-controls="accounts-tabpanel-3"
          />
          <Tab
            icon={<Star />}
            iconPosition="start"
            label="Onboarding Stats"
            id="accounts-tab-4"
            aria-controls="accounts-tabpanel-4"
          />
          <Tab
            icon={<AccessTime />}
            iconPosition="start"
            label="Recent Activity"
            id="accounts-tab-5"
            aria-controls="accounts-tabpanel-5"
          />
        </Tabs>
      </Box>
      {/* Financial Overview Tab */}
      <TabPanel value={currentTab} index={0}>
        {/* Top Metrics */}
        <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
          <Grid item xs={12} sm={6} md={6}>
            <Card
              elevation={2}
              sx={{ cursor: 'pointer' }}
              onClick={handleOpenSubscriptionModal}
            >
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Annual Recurring Revenue
                    </Typography>
                    <Typography variant="h4" component="div" color="primary">
                      {typeof dashboardData.arr.total_arr === 'number'
                        ? `$${dashboardData.arr.total_arr.toLocaleString()}`
                        : dashboardData.arr.total_arr}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {dashboardData.arr.active_subscriptions} active
                      {dashboardData.arr.paused_subscriptions > 0 &&
                        ` + ${dashboardData.arr.paused_subscriptions} paused`}{' '}
                      subscriptions
                    </Typography>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ fontStyle: 'italic', mt: 0.5 }}
                    >
                      Click to view details
                    </Typography>
                  </Box>
                  <TrendingUp
                    sx={{
                      fontSize: 40,
                      color: theme.palette.primary.main,
                      opacity: 0.7,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <MetricCard
              title="Current MRR"
              value={dashboardData.current_metrics.current_month_revenue}
              icon={CalendarToday}
              color="info"
              subtitle="Expected monthly revenue from active customers"
            />
          </Grid>
        </Grid>
        {/* Revenue Chart */}
        <Card elevation={2} sx={{ mb: 4 }}>
          <CardContent>
            <Tabs
              value={headlineChartTab}
              onChange={(_, value) => setHeadlineChartTab(value)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label="Revenue Forecast" />
              <Tab label="Historic ARR" />
              <Tab label="New Subs" />
            </Tabs>

            {headlineChartTab === 0 ? (
              <>
                <Typography variant="h5" gutterBottom>
                  Monthly Revenue & Subscription Forecast
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  All revenue is recognized immediately when charges are made.
                  Forecast includes both Xero recurring invoices and Stripe
                  subscription projections. Click on any revenue bar to see
                  detailed breakdown.
                </Typography>
                {revenueData.length > 0 ? (
                  <Box sx={{ height: 400, mt: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={revenueData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="displayMonth"
                          tick={{ fontSize: 12 }}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={70}
                        />
                        <YAxis
                          tickFormatter={formatCurrency}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip
                          formatter={formatTooltip}
                          labelFormatter={(label: string) => `Month: ${label}`}
                        />
                        <Legend />
                        <Bar
                          dataKey="revenue"
                          stackId="a"
                          fill={theme.palette.success.main}
                          name="Subscription Revenue"
                          onClick={handleRevenueBarClick}
                          style={{ cursor: 'pointer' }}
                        />
                        <Bar
                          dataKey="revenueData"
                          stackId="a"
                          fill={theme.palette.secondary.main}
                          name="Data Revenue"
                          onClick={handleRevenueBarClick}
                          style={{ cursor: 'pointer' }}
                        />
                        <Bar
                          dataKey="revenueOther"
                          stackId="a"
                          fill={theme.palette.info.main}
                          name="Other Revenue"
                          onClick={handleRevenueBarClick}
                          style={{ cursor: 'pointer' }}
                        />
                        <Bar
                          dataKey="forecast"
                          stackId="a"
                          fill={theme.palette.warning.main}
                          name="Forecast Revenue"
                          onClick={handleRevenueBarClick}
                          style={{ cursor: 'pointer' }}
                        />
                        <Bar
                          dataKey="clickableArea"
                          stackId="a"
                          fill="transparent"
                          onClick={handleRevenueBarClick}
                          style={{ cursor: 'pointer' }}
                          name=""
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No revenue data available
                    </Typography>
                  </Box>
                )}
              </>
            ) : headlineChartTab === 1 && arrMovementData.length > 0 ? (
              <>
                <Typography variant="h5" gutterBottom>
                  Historic ARR Movement
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  New ARR added, discounted ARR, pure cash ARR, lost ARR, and
                  net new ARR across all available billing history.
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={arrMovementData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="display_month"
                        tick={{ fontSize: 12 }}
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis
                        tickFormatter={formatCurrency}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={formatArrMovementTooltip}
                        labelFormatter={(label: string) => `Month: ${label}`}
                      />
                      <Legend />
                      <Bar
                        dataKey="discounted_arr"
                        stackId="new-arr"
                        fill={theme.palette.secondary.main}
                        name="Discounted ARR"
                      />
                      <Bar
                        dataKey="pure_arr"
                        stackId="new-arr"
                        fill={theme.palette.success.main}
                        name="Pure ARR"
                      />
                      <Bar
                        dataKey="lost_arr_negative"
                        fill={theme.palette.error.main}
                        name="Lost ARR"
                      />
                      <Line
                        type="monotone"
                        dataKey="net_new_arr"
                        stroke={theme.palette.primary.main}
                        strokeWidth={3}
                        dot={{ r: 3 }}
                        name="Net new ARR"
                      />
                      <Line
                        type="monotone"
                        dataKey="net_new_ex_discount_arr"
                        stroke={theme.palette.grey[700]}
                        strokeWidth={3}
                        strokeDasharray="6 4"
                        dot={{ r: 3 }}
                        name="Net new ARR ex discounts"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </>
            ) : headlineChartTab === 2 && arrMovementData.length > 0 ? (
              <>
                <Typography variant="h5" gutterBottom>
                  New Subscription Revenue
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Recognized subscription revenue from existing orgs compared
                  with pure new and discounted new orgs across all available
                  billing history, with new org counts split by discount level.
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={arrMovementData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="display_month"
                        tick={{ fontSize: 12 }}
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis
                        yAxisId="left"
                        tickFormatter={formatCurrency}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={formatArrMovementTooltip}
                        labelFormatter={(label: string) => `Month: ${label}`}
                      />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="existing_subscription_revenue"
                        stackId="subscription-revenue"
                        fill={theme.palette.success.dark}
                        name="Existing subscription revenue"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="pure_new_subscription_revenue"
                        stackId="subscription-revenue"
                        fill={theme.palette.success.light}
                        name="Pure new subscription revenue"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="discounted_new_subscription_revenue"
                        stackId="subscription-revenue"
                        fill={theme.palette.secondary.main}
                        name="Discounted new subscription revenue"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="new_org_count"
                        stroke={theme.palette.primary.main}
                        strokeWidth={3}
                        dot={{ r: 3 }}
                        name="Total new org count"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="pure_new_org_count"
                        stroke={theme.palette.success.main}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        name="Full-price org count"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="partially_discounted_new_org_count"
                        stroke={theme.palette.warning.main}
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={{ r: 2 }}
                        name="Partial-discount org count"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="fully_discounted_new_org_count"
                        stroke={theme.palette.error.main}
                        strokeWidth={2}
                        strokeDasharray="2 4"
                        dot={{ r: 2 }}
                        name="Full-discount org count"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No headline graph data available
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Subscription Breakdown Table */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              elevation={2}
              sx={{ cursor: 'pointer' }}
              onClick={handleOpenSubscriptionModal}
            >
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="h5">Subscription Breakdown</Typography>
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ fontStyle: 'italic' }}
                  >
                    Click to view detailed breakdown
                  </Typography>
                </Box>

                {subscriptionTableData.tiers.length > 0 ? (
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Tier</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Monthly</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Quarterly</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Yearly</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Total ARR</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subscriptionTableData.tiers
                          .sort((a, b) => {
                            // Sort order: Tier 1, Tier 2, Tier 3, Tier 4, Tier Micro, Custom
                            const getTierSortValue = (name: string) => {
                              if (name === 'Tier Micro') return 5
                              const tierMatch = name.match(/Tier (\d+)/)
                              if (tierMatch) {
                                return parseInt(tierMatch[1])
                              }
                              if (name === 'Custom') return 99
                              return 98
                            }

                            return getTierSortValue(a.name) - getTierSortValue(b.name)
                          })
                          .map((tier) => (
                            <TableRow key={tier.name} hover>
                              <TableCell component="th" scope="row">
                                <Typography variant="body1" fontWeight="medium">
                                  {tier.name}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {tier.monthly > 0 ? tier.monthly : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {tier.quarterly > 0 ? tier.quarterly : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {tier.yearly > 0 ? tier.yearly : '-'}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(tier.total_arr)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}

                        {/* Totals Row */}
                        <TableRow
                          sx={{ backgroundColor: theme.palette.grey[100] }}
                        >
                          <TableCell component="th" scope="row">
                            <Typography variant="body1" fontWeight="bold">
                              Total ARR Contribution
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {subscriptionTableData.totals.monthly > 0
                                ? subscriptionTableData.totals.monthly
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {subscriptionTableData.totals.quarterly > 0
                                ? subscriptionTableData.totals.quarterly
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {subscriptionTableData.totals.yearly > 0
                                ? subscriptionTableData.totals.yearly
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color="primary"
                            >
                              {formatCurrency(
                                subscriptionTableData.totals.total_arr
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    sx={{ mt: 2 }}
                  >
                    No subscription data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Billing Breakdown */}
        {dashboardData.billing_breakdown.length > 0 && (
          <Card elevation={2} sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Billing Breakdown
              </Typography>
              <Grid container spacing={2}>
                {dashboardData.billing_breakdown.map((billing, index) => {
                  const isAnnual = billing.billing_type === 'Yearly (Invoice)'
                  return (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Paper sx={{
                        p: 2,
                        textAlign: 'center',
                        ...(isAnnual ? { border: '2px solid', borderColor: 'primary.main' } : {}),
                      }}>
                        <Typography variant="h6" color="primary">
                          {billing.billing_type}
                        </Typography>
                        <Typography variant="h4">
                          {billing.customer_count}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          customers ({billing.percentage.toFixed(1)}%)
                        </Typography>
                      </Paper>
                    </Grid>
                  )
                })}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Annual Subscription History */}
        {dashboardData.annual_subscription_history && dashboardData.annual_subscription_history.length > 0 && (
          <Card elevation={2} sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Annual Subscriptions (6 Month Trend)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Currently {dashboardData.annual_subscription_history[dashboardData.annual_subscription_history.length - 1]?.annual_count} annual subscribers ({dashboardData.annual_subscription_history[dashboardData.annual_subscription_history.length - 1]?.annual_percentage}% of all paid accounts)
              </Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboardData.annual_subscription_history.map(d => ({
                    ...d,
                    month: new Date(d.month + '-01').toLocaleDateString('en-NZ', { month: 'short', year: '2-digit' }),
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 50]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="annual_count" name="Annual Subs" stroke="#1976d2" strokeWidth={2} dot={{ r: 4 }} />
                    <Line yAxisId="left" type="monotone" dataKey="total_count" name="Total Paid" stroke="#9e9e9e" strokeWidth={1} strokeDasharray="5 5" />
                    <Line yAxisId="right" type="monotone" dataKey="annual_percentage" name="Annual %" stroke="#2e7d32" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        )}
      </TabPanel>
      {/* Xero Integration Tab */}
      <TabPanel value={currentTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <XeroConnectionStatus />
          </Grid>
        </Grid>
      </TabPanel>
      {/* Reconciliation Tab */}
      <TabPanel value={currentTab} index={2}>
        <ReconciliationDashboard />
      </TabPanel>
      {/* Discount Codes Tab */}
      <TabPanel value={currentTab} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <DiscountCodesManagement
              onDataChange={handleDataChange}
              refreshTrigger={refreshTrigger}
            />
          </Grid>
          <Grid item xs={12}>
            <ActiveDiscountsView
              onDataChange={handleDataChange}
              refreshTrigger={refreshTrigger}
            />
          </Grid>
          <Grid item xs={12}>
            <ExpiredDiscountsView />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Onboarding Tab */}
      <TabPanel value={currentTab} index={4}>
        <OnboardingStatsDashboard />
      </TabPanel>

      {/* Recent Activity Tab */}
      <TabPanel value={currentTab} index={5}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Activity Feed
          </Typography>
          <ActivityFeedAll pageSize={20} showTitle={false} />
        </Box>
      </TabPanel>

      {/* Revenue Breakdown Modal */}
      <RevenueBreakdownModal
        open={modalOpen}
        onClose={handleCloseModal}
        month={selectedMonth}
        data={revenueBreakdownData}
        loading={modalLoading}
        error={modalError}
        onMonthChange={loadRevenueBreakdown}
      />
      {/* Subscription Breakdown Modal */}
      <SubscriptionBreakdownModal
        open={subscriptionModalOpen}
        onClose={handleCloseSubscriptionModal}
        data={subscriptionBreakdownData}
        loading={subscriptionModalLoading}
        error={subscriptionModalError}
      />
    </Box>
  )
}

export default AccountsDashboard
